import axios from 'axios';
import { SinhVien, ApiResponse, PaginationInfo } from '../../../shared/types/database';
import { 
  SinhVienListParams, 
  CreateSinhVienRequest, 
  UpdateSinhVienRequest 
} from '../../../shared/types/api';

const API_BASE = '/api/v1/sinhvien';

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: PaginationInfo;
  message?: string;
  error?: string;
}

class SinhVienService {
  // ============== MAIN CRUD OPERATIONS ==============
  
  // Get students list with pagination and filters
  async getList(params: SinhVienListParams): Promise<PaginatedResponse<SinhVien>> {
    const response = await axios.get<PaginatedResponse<SinhVien>>(API_BASE, { params });
    return response.data;
  }

  // Get single student by ID
  async getById(id: string): Promise<ApiResponse<SinhVien>> {
    const response = await axios.get<ApiResponse<SinhVien>>(`${API_BASE}/${id}`);
    return response.data;
  }

  // Create new student
  async create(data: CreateSinhVienRequest): Promise<ApiResponse<void>> {
    const response = await axios.post<ApiResponse<void>>(API_BASE, data);
    return response.data;
  }

  // Update existing student
  async update(id: string, data: UpdateSinhVienRequest): Promise<ApiResponse<void>> {
    const response = await axios.put<ApiResponse<void>>(`${API_BASE}/${id}`, data);
    return response.data;
  }

  // Delete student
  async delete(id: string): Promise<ApiResponse<void>> {
    const response = await axios.delete<ApiResponse<void>>(`${API_BASE}/${id}`);
    return response.data;
  }

  // ============== BULK OPERATIONS ==============
  
  // Sync students from legacy system
  async sync(lopHocId: string, safeMode: boolean = true): Promise<ApiResponse<{ 
    inserted: number; 
    updated: number; 
    deleted?: number; 
    preserved?: number; 
    invalidStudents?: number; 
    safeMode?: boolean; 
  }>> {
    const response = await axios.post<ApiResponse<{ 
      inserted: number; 
      updated: number; 
      deleted?: number; 
      preserved?: number; 
      invalidStudents?: number; 
      safeMode?: boolean; 
    }>>(
      `${API_BASE}/sync`,
      { lopHocId, safeMode }
    );
    return response.data;
  }

  // Import students from Excel
  async importFromExcel(items: CreateSinhVienRequest[]): Promise<ApiResponse<{ inserted: number; updated: number; errors?: string[] }>> {
    const response = await axios.post<ApiResponse<{ inserted: number; updated: number; errors?: string[] }>>(
      `${API_BASE}/import`,
      { items }
    );
    return response.data;
  }

  // Sync all students from secondary database
  async syncAll(): Promise<ApiResponse> {
    try {
      const response = await axios.post<ApiResponse>(`${API_BASE}/sync-all`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Sync students by class from secondary database
  async syncStudentsByClass(lopHocPhanId: string): Promise<ApiResponse<{ count: number; skippedMissing: number }>> {
    try {
      // Call the correct API endpoint for syncing class section students
      const response = await axios.post<ApiResponse<{ count: number; skippedMissing: number }>>(
        `/api/v1/lophocphan-sinhvien/${lopHocPhanId}/sinhvien/sync`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Bulk sync students for all class sections in a course and subject
  async bulkSyncStudentsBySubject(khoaHocId: string, monHocId: string): Promise<ApiResponse<{
    totalClasses: number;
    synced: number;
    totalStudents: number;
    details: Array<{ LopHocPhanID: string; count: number; status: 'ok' | 'error' }>;
  }>> {
    try {
      const response = await axios.post<ApiResponse<any>>(
        `/api/v1/lophocphan-sinhvien/bulk-sync`,
        { KhoaHocID: khoaHocId, MonHocID: monHocId }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // ============== HELPER METHODS ==============
  
  // Test connection
  async testConnection(): Promise<ApiResponse<any>> {
    const response = await axios.get<ApiResponse<any>>(`${API_BASE}/test`);
    return response.data;
  }

  // Get students by class
  async getByClass(lopHocId: string, params?: Partial<SinhVienListParams>): Promise<PaginatedResponse<SinhVien>> {
    const fullParams = { ...params, lopHocId };
    return this.getList(fullParams);
  }

  // Alias for getByClass
  async getStudentsByClass(lopHocId: string, params?: Partial<SinhVienListParams>): Promise<PaginatedResponse<SinhVien>> {
    return this.getByClass(lopHocId, params);
  }

  // Get students by course
  async getByCourse(khoaHocId: string, params?: Partial<SinhVienListParams>): Promise<PaginatedResponse<SinhVien>> {
    const fullParams = { ...params, khoaHocId };
    return this.getList(fullParams);
  }

  // Search students
  async search(searchTerm: string, params?: Partial<SinhVienListParams>): Promise<PaginatedResponse<SinhVien>> {
    const fullParams = { ...params, search: searchTerm };
    return this.getList(fullParams);
  }
}

// Export singleton instance
export const sinhVienService = new SinhVienService();
export default sinhVienService; 
